/**
 * PartnerSearchAutocomplete Component
 * Searchable dropdown with inline partner creation (similar to CustomerSearchAutocomplete)
 */

import React, { useState, useCallback, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Alert,
} from "@mui/material";
import { Add, Business } from "@mui/icons-material";
import { getPartners, createPartner } from "../../features/partners/services/partnerService";

/**
 * PartnerSearchAutocomplete Component
 * @param {Object} props
 * @param {Object|null} props.value - Selected partner object or partnerId string
 * @param {Function} props.onChange - Callback when partner is selected/changed
 * @param {string} [props.label] - Label for the field
 * @param {boolean} [props.required] - Whether field is required
 * @param {boolean} [props.disabled] - Whether field is disabled
 * @param {Object} [props.error] - Error state
 */
const PartnerSearchAutocomplete = ({
  value,
  onChange,
  label = "Partner",
  required = false,
  disabled = false,
  error = null,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newPartnerData, setNewPartnerData] = useState({
    name: "",
    contactPerson: "",
    phone: "",
    email: "",
    address: "",
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [allPartners, setAllPartners] = useState([]);

  const loadPartners = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getPartners({ isActive: true });
      if (result.success) {
        setAllPartners(result.data || []);
      } else {
        setAllPartners([]);
      }
    } catch (err) {
      console.error("Error loading partners:", err);
      setAllPartners([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (disabled) return;
    loadPartners();
  }, [disabled, loadPartners]);

  const filterPartners = useCallback((searchTerm, partners) => {
    if (!searchTerm || searchTerm.trim().length === 0) {
      return partners;
    }
    const searchLower = searchTerm.toLowerCase();
    return partners.filter(
      (partner) =>
        partner.name?.toLowerCase().includes(searchLower) ||
        partner.contactPerson?.toLowerCase().includes(searchLower) ||
        partner.phone?.includes(searchTerm)
    );
  }, []);

  useEffect(() => {
    setOptions(filterPartners(inputValue, allPartners));
  }, [inputValue, allPartners, filterPartners]);

  // Handle creating new partner
  const handleCreatePartner = async () => {
    if (!newPartnerData.name.trim()) {
      setCreateError("Partner name is required");
      return;
    }
    if (!newPartnerData.contactPerson.trim()) {
      setCreateError("Contact person is required");
      return;
    }
    if (!newPartnerData.phone.trim()) {
      setCreateError("Phone number is required");
      return;
    }

    setCreating(true);
    setCreateError(null);

    try {
      const result = await createPartner({
        name: newPartnerData.name.trim(),
        contactPerson: newPartnerData.contactPerson.trim(),
        phone: newPartnerData.phone.trim(),
        email: newPartnerData.email.trim() || undefined,
        address: newPartnerData.address.trim() || undefined,
      });

      if (result.success) {
        const newPartner = result.data;
        onChange(newPartner);
        setAllPartners((prev) => [...prev, newPartner]);
        setCreateDialogOpen(false);
        setNewPartnerData({
          name: "",
          contactPerson: "",
          phone: "",
          email: "",
          address: "",
        });
        setInputValue(newPartner.name);
      } else {
        setCreateError(result.error || "Failed to create partner");
      }
    } catch (err) {
      setCreateError(err.message || "Failed to create partner");
    } finally {
      setCreating(false);
    }
  };

  // Check if we should show "Add new" option
  const showAddNewOption =
    inputValue.trim().length >= 1 &&
    !options.some(
      (opt) => opt.name?.toLowerCase() === inputValue.toLowerCase()
    );

  // Combined options including "Add new" option
  const allOptions = showAddNewOption
    ? [
        {
          id: "__NEW__",
          name: `Add "${inputValue}"`,
          isNew: true,
        },
        ...options,
      ]
    : options;

  // Get display value
  const displayValue = typeof value === "string" 
    ? options.find((opt) => opt.id === value) || null
    : value;

  // Update input value when displayValue changes (when partner is selected)
  useEffect(() => {
    if (displayValue && displayValue.name) {
      setInputValue(displayValue.name);
    } else if (!displayValue) {
      setInputValue("");
    }
  }, [displayValue]);

  return (
    <>
      <Autocomplete
        disabled={disabled}
        value={displayValue}
        onChange={(event, newValue) => {
          if (newValue && newValue.isNew) {
            // User wants to create new partner
            setNewPartnerData((prev) => ({ ...prev, name: inputValue.trim() }));
            setCreateDialogOpen(true);
          } else {
            onChange(newValue);
            // Update input value to show selected partner name immediately
            if (newValue && newValue.name) {
              setInputValue(newValue.name);
            } else if (!newValue) {
              setInputValue("");
            }
          }
        }}
        inputValue={inputValue}
        onInputChange={(event, newInputValue, reason) => {
          // Update inputValue when user types or clears
          // Don't update when reason is "reset" (happens automatically on selection)
          if (reason === "input" || reason === "clear") {
            setInputValue(newInputValue);
          }
        }}
        onOpen={() => {
          if (allPartners.length === 0) {
            loadPartners();
          }
        }}
        options={allOptions}
        getOptionLabel={(option) => {
          if (option.isNew) return option.name;
          return option.name || "";
        }}
        isOptionEqualToValue={(option, value) => {
          return option.id === value?.id;
        }}
        loading={loading}
        filterOptions={(x) => x} // Disable client-side filtering, we do server-side
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={required}
            error={!!error}
            helperText={error}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          if (option.isNew) {
            return (
              <Box
                component="li"
                {...props}
                sx={(theme) => ({
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  py: 1.5,
                  px: 2,
                  borderTop: "1px solid",
                  borderColor: theme.palette.divider,
                  backgroundColor: theme.palette.action.hover,
                })}
              >
                <Add color="primary" />
                <Typography variant="body2" color="primary">
                  {option.name}
                </Typography>
              </Box>
            );
          }

          return (
            <Box component="li" {...props} sx={{ py: 1.5, px: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Business fontSize="small" color="action" />
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {option.name}
                  </Typography>
                  {option.contactPerson && (
                    <Typography variant="caption" color="text.secondary">
                      Contact: {option.contactPerson}
                    </Typography>
                  )}
                  {option.phone && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {option.phone}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          );
        }}
        noOptionsText={
          inputValue.trim().length < 2
            ? "Type at least 2 characters to search"
            : "No partners found"
        }
      />

      {/* Create Partner Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => {
          if (!creating) {
            setCreateDialogOpen(false);
            setCreateError(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Partner</DialogTitle>
        <DialogContent>
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createError}
            </Alert>
          )}

          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Partner Name"
                  value={newPartnerData.name}
                  onChange={(e) =>
                    setNewPartnerData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  required
                  autoFocus
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Person"
                  value={newPartnerData.contactPerson}
                  onChange={(e) =>
                    setNewPartnerData((prev) => ({ ...prev, contactPerson: e.target.value }))
                  }
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={newPartnerData.phone}
                  onChange={(e) =>
                    setNewPartnerData((prev) => ({ ...prev, phone: e.target.value }))
                  }
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={newPartnerData.email}
                  onChange={(e) =>
                    setNewPartnerData((prev) => ({ ...prev, email: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  multiline
                  rows={2}
                  value={newPartnerData.address}
                  onChange={(e) =>
                    setNewPartnerData((prev) => ({ ...prev, address: e.target.value }))
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateDialogOpen(false);
              setCreateError(null);
            }}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreatePartner}
            variant="contained"
            disabled={creating}
          >
            {creating ? <CircularProgress size={20} /> : "Create Partner"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PartnerSearchAutocomplete;

